import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AddMnRuleModal from "../../../../components/modals/addMnRuleModal/addMnRuleModal";
import { MedicationItem } from "../../../../data-model/drugConfigModel";
import { fetchDrugConfigfromFirebase } from "../../../../redux/slice/drugConfigSlice";
import { AppDispatch, RootState } from "../../../../redux/store/store";
import MnControlsBar from "./components/MnControlsBar";
import MnFooter from "./components/MnFooter";
import MnRulesContent from "./components/MnRulesContent";
import { useMedicalNecessityRules } from "./hooks/useMedicalNecessityRules";
import { ConfigurationType, SubRuleType } from "./utils/types";

const MedicalNecessityConfiguration = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { data: drugConfig } = useSelector(
    (state: RootState) => state.drugConfig,
  );

  const hook = useMedicalNecessityRules();

  // ── Search state per panel ─────────────────────────────────────────────
  const [mcqSearch, setMcqSearch] = useState("");
  const [subjectiveSearch, setSubjectiveSearch] = useState("");

  // ── Add modal state (editing is now inline) ────────────────────────────
  const [modalOpen, setModalOpen] = useState(false);
  const [modalPanelLabel, setModalPanelLabel] = useState("");
  const [modalSubType, setModalSubType] = useState<SubRuleType | "flat">("mcq");

  // ── Fetch drug list when Drug Specific is active ───────────────────────
  useEffect(() => {
    if (hook.configurationType === "drug_specific") {
      dispatch(fetchDrugConfigfromFirebase(hook.organization) as any);
    }
  }, [hook.organization, hook.configurationType, dispatch]);

  // ── Drug dropdown options ──────────────────────────────────────────────
  const drugOptions = useMemo(
    () =>
      (drugConfig?.medications || []).map((drug: MedicationItem) => ({
        label: drug.display_name,
        value: drug.name,
      })),
    [drugConfig],
  );

  // ── Clear search on rule type change ──────────────────────────────────
  useEffect(() => {
    setMcqSearch("");
    setSubjectiveSearch("");
  }, [hook.ruleType]);

  // ── Add modal handlers ─────────────────────────────────────────────────
  const openAddModal = (subType: SubRuleType | "flat", panelLabel: string) => {
    setModalSubType(subType);
    setModalPanelLabel(panelLabel);
    setModalOpen(true);
  };

  return (
    <div className="medical-necessity-configuration flex h-full flex-col overflow-hidden">
      <div className="flex h-full flex-col gap-4 overflow-hidden rounded bg-white p-4">
        {/* Header */}
        <div className="flex flex-shrink-0 items-center border-b border-primaryGray-16 pb-4">
          <div className="text-base font-bold text-primaryGray-1">
            Medical Necessity Configuration
          </div>
        </div>

        {/* Controls */}
        <MnControlsBar
          organization={hook.organization}
          onOrganizationChange={hook.setOrganization}
          configurationType={hook.configurationType}
          onConfigurationTypeChange={(val: ConfigurationType) => {
            hook.setConfigurationType(val);
            if (val === "generic") hook.setSelectedDrugId(null);
          }}
          drugOptions={drugOptions}
          selectedDrugId={hook.selectedDrugId}
          onDrugChange={hook.setSelectedDrugId}
          ruleType={hook.ruleType}
          onRuleTypeChange={hook.setRuleType}
        />

        {/* Content */}
        <MnRulesContent
          isLoading={hook.isLoading}
          error={hook.error}
          configurationType={hook.configurationType}
          selectedDrugId={hook.selectedDrugId}
          ruleType={hook.ruleType}
          mcqRules={hook.mcqRules}
          subjectiveRules={hook.subjectiveRules}
          mcqSearch={mcqSearch}
          onMcqSearchChange={setMcqSearch}
          subjectiveSearch={subjectiveSearch}
          onSubjectiveSearchChange={setSubjectiveSearch}
          onAddRule={openAddModal}
          onEditRule={hook.editRule}
          onDeleteRule={hook.deleteRule}
        />

        {/* Footer */}
        <MnFooter
          updatedAt={hook.updatedAt}
          updatedBy={hook.updatedBy}
          hasChanges={hook.hasChanges}
          isSaving={hook.isSaving}
          onSave={hook.saveChanges}
        />
      </div>

      {/* Add Rule Modal */}
      {modalOpen && (
        <AddMnRuleModal
          panelLabel={modalPanelLabel}
          onClose={() => setModalOpen(false)}
          onSave={(ruleText) => hook.addRule(modalSubType, ruleText)}
        />
      )}
    </div>
  );
};

export default MedicalNecessityConfiguration;
